import { FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseCategoryRequestModel } from "app/based/baseApiModel/baseCategory/BaseCategoryRequestModel";
import { BaseCategoryResponseModel } from "app/based/baseApiModel/baseCategory/BaseCategoryResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { NiceSimRegistrationInjection } from "features/niceSimRegistration/di/NiceSimRegistrationInjection";
import { INiceSimRegistrationRepository } from "features/niceSimRegistration/domain/repositories/INiceSimRegistrationRepository";
import isEmpty from "lodash/isEmpty";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { NiceSimRegistrationQueryEntity } from "../entities/NiceSimRegistrationEntity";
import { NiceSimRegistrationItemEntity } from "../entities/NiceSimRegistrationItemEntity";

@injectable()
export class NiceSimRegistrationUseCase {
  private niceSimRegistrationRepo: INiceSimRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.niceSimRegistrationRepo =
      NiceSimRegistrationInjection.getNiceSimRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  async getList(
    params: NiceSimRegistrationQueryEntity
  ): Promise<ListBaseApiResponseModel<NiceSimRegistrationItemEntity>> {
    return await this.niceSimRegistrationRepo.getList(params);
  }

  async getDetail(
    params: NiceSimRegistrationQueryEntity
  ): Promise<BaseApiResponseModel<NiceSimRegistrationItemEntity>> {
    return await this.niceSimRegistrationRepo.getDetail(params);
  }

  async exportReport(
    params: NiceSimRegistrationQueryEntity
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.niceSimRegistrationRepo.exportReport(params);
  }

  async checkStatus(
    code: string
  ): Promise<BaseApiResponseModel<NiceSimRegistrationItemEntity>> {
    return await this.niceSimRegistrationRepo.checkStatus(code);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination" | "package"
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = FILTER_FEATURE.CARRIER_LIST;
        break;
      case "partner":
        path = FILTER_FEATURE.PARTNER_LIST;
        break;
      case "store":
        path = FILTER_FEATURE.STORE_LIST;
        break;
      case "denomination":
        path = FILTER_FEATURE.DENOMINATION_LIST;
        break;
      case "package":
        path = FILTER_FEATURE.PACKAGE_LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }

  async getNiceSimCategory(
    params: BaseCategoryRequestModel,
    category: "zodiac" | "numerology" | "destiny" | "numberType"
  ): Promise<Array<BaseCategoryResponseModel>> {
    let path = "";
    let savedKey = "";
    switch (category) {
      case "zodiac":
        path = FILTER_FEATURE.ZODIAC_LIST;
        savedKey = CacheKeyEnum.zodiac;
        break;
      case "numerology":
        path = FILTER_FEATURE.NUMEROLOGY_LIST;
        savedKey = CacheKeyEnum.numerology;
        break;
      case "destiny":
        path = FILTER_FEATURE.DESTINY_LIST;
        savedKey = CacheKeyEnum.destiny;
        break;
      case "numberType":
        path = FILTER_FEATURE.NUMER_TYPE_LIST;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
      savedKey: !isEmpty(savedKey) && savedKey,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B"
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    let capabilityKey = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        capabilityKey = CapabilityEnum.staffFilter;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      capabilityKey: !isEmpty(capabilityKey) && capabilityKey,
    });
  }
}
